import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MARKETPLACES, buscaPorMarketplace } from '../../design/marketplaces';
import { registrarFiltro } from '../../inteligencia/coleta';

/**
 * Atalhos por loja, logo abaixo do hero.
 *
 * Cada atalho é um tile com a identidade real da loja: fundo na cor da marca e
 * o wordmark oficial centralizado, sem texto de apoio. Marca, cores e tamanho do
 * logo vêm de `MARKETPLACES[].marca`; a borda e a sombra por tema ficam em
 * `.sv-loja-tile` (index.css).
 *
 * Cada atalho leva à busca filtrada pelo slug canônico, o mesmo que o backend
 * classifica. Não mostra contagem de ofertas de propósito: contar por
 * marketplace é a consulta mais cara do catálogo, e essa informação já vive no
 * bloco de Destaques por loja, que depende da flag `promotions_hub`.
 */
export default function MarketplaceStrip({ esqueleto = false }) {
    // Celular: o pedaço do próximo tile na borda direita aparece esmaecido (máscara
    // em .sv-loja-trilho), para indicar continuidade sem o corte seco. No fim do
    // trilho a máscara sai da direita e passa para a esquerda.
    const trilhoRef = useRef(null);
    const [borda, setBorda] = useState({ inicio: true, fim: false });
    const medirBorda = useCallback(() => {
        const el = trilhoRef.current;
        if (!el) return;
        const inicio = el.scrollLeft <= 4;
        const fim = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
        setBorda((atual) => (atual.inicio === inicio && atual.fim === fim ? atual : { inicio, fim }));
    }, []);
    useEffect(() => {
        if (esqueleto) return undefined;
        medirBorda();
        window.addEventListener('resize', medirBorda);
        return () => window.removeEventListener('resize', medirBorda);
    }, [esqueleto, medirBorda]);

    // Enquanto as flags carregam, o espaço exato da faixa já fica reservado: sem
    // isso ela aparecia depois do hero e empurrava o resto da Home.
    if (esqueleto) {
        return (
            <div className="mt-7 sm:mt-8" aria-hidden="true" data-testid="marketplace-strip-esqueleto">
                <div className="h-[22px] mb-3" />
                <ul className="sv-rail sv-loja-trilho flex gap-3 overflow-hidden -mx-4 px-4 pb-1 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4">
                    {MARKETPLACES.map((m) => (
                        <li key={m.slug} className="shrink-0 w-[44%] sm:w-auto">
                            <div className="sv-skeleton sv-loja-tile" />
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
    return (
        <nav aria-label="Explorar por loja" className="mt-7 sm:mt-8" data-testid="marketplace-strip">
            <p className="text-[15px] font-bold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
                Explorar por loja
            </p>
            <ul ref={trilhoRef} onScroll={medirBorda} data-inicio={borda.inicio} data-fim={borda.fim}
                className="sv-rail sv-loja-trilho flex gap-3 overflow-x-auto -mx-4 px-4 pb-1 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:overflow-visible">
                {MARKETPLACES.map((m) => (
                    <li key={m.slug} className="shrink-0 w-[44%] sm:w-auto">
                        <Link
                            to={buscaPorMarketplace(m.slug)}
                            onClick={() => registrarFiltro('store', m.slug)}
                            className="sv-loja-tile"
                            style={{ '--loja-fundo': m.marca.fundo, '--loja-apoio-rgb': m.marca.apoioRgb, '--loja-logo-h': `${m.marca.alturaLogo}px` }}
                            aria-label={`Ver ofertas ${m.label}`}
                            data-testid={`marketplace-${m.slug}`}
                        >
                            <img src={m.marca.logo} alt={m.label} className="sv-loja-logo" draggable="false" />
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
